<!--
  =====================================================================
  FILIALI SUB PAGE - BRANCH DETAIL VIEW
  =====================================================================
  
  Displays detailed information about a specific branch office.
  
  FEATURES:
  - Dynamic filiale (branch) lookup from URL slug
  - Displays all branch information (address, contact, hours, etc.)
  - Responsive two-column layout for info and hours
  - Unified hours display (supports both 'orario' and 'hours' fields)
  - Intelligent day ordering (lunedi-domenica)
  - HTML content sanitization (strips iframes and map containers)
  - Back button with smart visibility management
  - Shows loading/error/not-found states
  - Dynamic section rendering
  - Mobile-responsive design
  
  URL PATTERN: /cassa/filiali/:slug
  
  =====================================================================
-->

<template>
  <div>
    <SiteHeader />
    
    <div class="back-button-wrapper" v-show="!isMegamenuOpen && !isFooterButtonVisible">
      <a href="/cassa/filiali" class="back-button">
        <span class="arrow">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        Torna alle filiali
      </a>
    </div>
    
    <main class="filiale-detail">
      <div v-if="state.loading" class="loading-state">Caricamento...</div>
      
      <div v-else-if="state.error" class="error-state">Errore durante il caricamento della filiale</div>
      
      <div v-else>
        <div class="filiale-container">
          <div class="filiale-header">
            <h1 class="filiale-title">{{ state.page.title }}</h1>
          </div>
        </div>

        <div v-if="state.page.image" class="filiale-hero-image">
          <SectionHtmlText :content="{ image: state.page.image }" />
        </div>

        <div class="filiale-container">
          <div class="filiale-info">
            <div class="info-row info-row--with-map">
              <span class="info-label">{{ state.page.addressLabel || 'Indirizzo' }}</span>
              <span class="info-value info-value--with-map">
                <span class="address-text">
                  {{ state.page.address }}<br>
                  {{ state.page.cap }} {{ state.page.city }}
                </span>
                <a v-if="state.page.mapsUrl" :href="state.page.mapsUrl" target="_blank" rel="noopener noreferrer" class="maps-link-inline" :title="`Visualizza ${state.page.title} su mappa`">
                  <img src="/images/logos/position.png" alt="Mappa" class="maps-pin-icon" />
                  <span>Visualizza su mappa</span>
                </a>
              </span>
            </div>
            
            <div class="info-row" v-if="state.page.phone">
              <span class="info-label">{{ state.page.phoneLabel || 'Telefono' }}</span>
              <span class="info-value">{{ state.page.phone }}</span>
            </div>
            
            <div v-if="state.page.fax" class="info-row">
              <span class="info-label">{{ state.page.faxLabel || 'Fax' }}</span>
              <span class="info-value">{{ state.page.fax }}</span>
            </div>
            
            <div v-if="state.page.email" class="info-row">
              <span class="info-label">{{ state.page.emailLabel || 'Email' }}</span>
              <span class="info-value">{{ state.page.email }}</span>
            </div>
            
            <div v-if="state.page.responsabile" class="info-row">
              <span class="info-label">{{ state.page.responsabileLabel || 'Responsabile' }}</span>
              <span class="info-value">{{ state.page.responsabile }}</span>
            </div>
            
            <div v-if="state.page.text" class="info-row info-row--no-label">
              <span class="info-value">{{ state.page.text }}</span>
            </div>
          </div>
        </div>

        <div class="filiale-container" v-if="hasHours">
          <div class="filiale-orari">
            <h2 class="orari-title">{{ state.page.orariTitle || 'Orari' }}</h2>
            <div class="orari-grid">
              <div
                class="orari-row"
                v-for="row in normalizedHours"
                :key="row.key"
              >
                <span class="orari-label">{{ row.label }}</span>
                <span class="orari-time">{{ row.value }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="filiale-sections">
          <component
            v-for="(section, index) in cleanedSections"
            :key="index"
            :is="getComponent(section.type)"
            :content="section.content"
          />
        </div>
      </div>
    </main>

    <SiteFooter />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed } from 'vue'
import { state, loadPageContent, setFilialePageData, setNotFound } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// SHARED LAYOUT COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SiteHeader from '@/components/sections/shared/SiteHeader.vue'
import SiteFooter from '@/components/sections/shared/SiteFooter.vue'

// ───────────────────────────────────────────────────────────────────
// HTML-BASED SECTION COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SectionHtmlText from '@/components/sections/html/SectionHtmlText.vue'

// ───────────────────────────────────────────────────────────────────
// COMPONENT STATE
// ───────────────────────────────────────────────────────────────────

const isMegamenuOpen = ref(false)
const isFooterButtonVisible = ref(false)

// ───────────────────────────────────────────────────────────────────
// COMPONENT RESOLVER
// ───────────────────────────────────────────────────────────────────

/**
 * Maps section content types to Vue components
 * Currently only supports HTML text sections for filiale detail
 * 
 * @param {string} type - Section type identifier
 * @returns {VueComponent} Component to render
 */
function getComponent(type) {
  if (type === 'html-text') return SectionHtmlText
  return SectionHtmlText
}

// ───────────────────────────────────────────────────────────────────
// FILIALE DATA - Already loaded from state.page via setFilialePageData
// ───────────────────────────────────────────────────────────────────
// No computed needed - data comes from state.page which is populated
// by setFilialePageData in onMounted hook

// ───────────────────────────────────────────────────────────────────
// BUSINESS HOURS NORMALIZATION
// ───────────────────────────────────────────────────────────────────

/**
 * Day ordering for consistent display (Italian week order)
 */
const DAY_ORDER = ['lunedi','martedi','mercoledi','giovedi','venerdi','sabato','domenica']

/**
 * Day labels mapping (Italian translations)
 */
const DAY_LABELS = {
  lunedi: 'Lunedì',
  martedi: 'Martedì',
  mercoledi: 'Mercoledì',
  giovedi: 'Giovedì',
  venerdi: 'Venerdì',
  sabato: 'Sabato',
  domenica: 'Domenica'
}

/**
 * Gets raw hours data from state.page
 * Supports both 'orario' and 'hours' field names
 * 
 * @returns {Object|null} Hours object or null
 */
const rawHours = computed(() => {
  return state.page?.orario || state.page?.hours || null
})

/**
 * Determines if filiale has business hours data
 * 
 * @returns {boolean} True if hours object has entries
 */
const hasHours = computed(() => !!rawHours.value && Object.keys(rawHours.value).length > 0)

/**
 * Normalizes and orders business hours for display
 * Filters out empty/null entries and applies Italian day ordering
 * 
 * @returns {Array} Array of {key, label, value} objects
 */
const normalizedHours = computed(() => {
  if (!rawHours.value) return []
  return DAY_ORDER
    .filter(k => {
      const v = rawHours.value[k]
      if (v == null) return false
      const s = String(v).trim()
      return s.length > 0
    })
    .map(k => ({
      key: k,
      label: DAY_LABELS[k] || k,
      value: String(rawHours.value[k]).trim()
    }))
})

// ───────────────────────────────────────────────────────────────────
// HTML CONTENT SANITIZATION
// ───────────────────────────────────────────────────────────────────

/**
 * Removes potentially problematic HTML elements from content
 * Strips iframes and map container divs to prevent layout issues
 * 
 * @param {string} html - The HTML content to clean
 * @returns {string} Sanitized HTML
 */
function stripIframes(html = '') {
  if (typeof html !== 'string' || !html) return ''
  // Remove all iframe tags and their content
  let clean = html.replace(/<iframe[\s\S]*?<\/iframe>/gi, '')
  // Remove map container divs
  clean = clean.replace(/<div[^>]*class=(["'])[^"']*map-container[^"']*\1[^>]*>[\s\S]*?<\/div>/gi, '')
  return clean
}

/**
 * Cleans page sections by removing problematic HTML from html-text sections
 * 
 * @returns {Array} Array of cleaned section objects
 */
const cleanedSections = computed(() => {
  const sections = Array.isArray(state.pageSections) ? state.pageSections : []
  return sections.map(s => {
    if (s?.type === 'html-text' && s?.content?.text) {
      return { 
        ...s, 
        content: { 
          ...s.content, 
          text: stripIframes(s.content.text) 
        } 
      }
    }
    return s
  })
})

// ───────────────────────────────────────────────────────────────────
// BACK BUTTON VISIBILITY MANAGEMENT
// ───────────────────────────────────────────────────────────────────

/**
 * Checks megamenu and footer visibility to control back button display
 * Hides top back button if footer has visible back button
 */
const checkMegamenu = () => {
  const megamenu = document.querySelector('.mega-menu')
  isMegamenuOpen.value = !!megamenu
  
  const footerButton = document.querySelector('.filiale-footer .back-button')
  if (footerButton) {
    const rect = footerButton.getBoundingClientRect()
    isFooterButtonVisible.value = rect.top >= 0 && rect.top <= window.innerHeight
  } else {
    isFooterButtonVisible.value = false
  }
}

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

// Observer instance for visibility tracking (top-level declaration)
let observer = null

/**
 * Initialize page on component mount
 * - Extract filiale slug from URL
 * - Load filiale index from store if not already loaded
 * - Set current filiale page data via centralized state.page
 * - Setup DOM observers for visibility management
 * - Trigger 404 if filiale not found
 */
onMounted(async () => {
  // Parse URL: /cassa/filiali/:slug
  const pathSegments = window.location.pathname.split('/').filter(Boolean)
  const segments = pathSegments[0] === 'cassa' ? pathSegments.slice(1) : pathSegments
  const slug = segments[1]
  
  // Load filiale index from /filiali endpoint
  await loadPageContent('/filiali')
  
  // Set current filiale data in state.page
  const found = setFilialePageData(slug)
  
  // If filiale not found, trigger centralized 404 handling
  if (!found) {
    setNotFound()
    return
  }
  
  // Setup DOM observer for visibility tracking
  observer = new MutationObserver(checkMegamenu)
  observer.observe(document.body, { 
    childList: true,
    attributes: true, 
    attributeFilter: ['class'],
    subtree: true 
  })

  // Setup scroll/resize event listeners
  window.addEventListener('scroll', checkMegamenu)
  window.addEventListener('resize', checkMegamenu)
  setTimeout(checkMegamenu, 100)
})

/**
 * Cleanup on component unmount
 * Disconnects observer and removes event listeners
 */
onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
  window.removeEventListener('scroll', checkMegamenu)
  window.removeEventListener('resize', checkMegamenu)
})
</script>

<style scoped lang="scss">
@import '@/assets/styles/partials/variables';
@import '@/assets/styles/partials/compat';

/* ------------------------------------------------------------------
   Bottone torna indietro fisso
------------------------------------------------------------------ */
.back-button-wrapper {
  position: fixed;
  top: calc(var(--header-total-height) + 1.2rem);
  left: 1rem;
  z-index: 150;

  @media (max-width: 1300px) {
    top: calc(var(--header-total-height) + 1.2rem);
    left: 1rem;
  }

  @media (max-width: 920px) {
    display: none;
  }

  @media (max-width: 768px) {
    left: 0;
  }
}

.back-button {
  font-size: clamp(0.95rem, 2.5vw, 1.15rem);
  font-weight: 600;
  color: $clr-brand-dark;
  text-decoration: none;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  @include flex-gap(0.35rem);
  line-height: 1;
  padding: 0.6rem 0;

  @media (hover: hover) {
    &:hover {
      color: $clr-tertiary;
      transform: scale(1.05);
    }

    &:hover .arrow {
      transform: rotate(-90deg);
    }
  }

  .arrow {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1em;
    height: 1em;
    transition: transform 0.3s ease;
    transform: rotate(90deg);

    svg {
      width: 100%;
      height: 100%;
      display: block;
    }
  }
}

/* ------------------------------------------------------------------
   Contenuto principale
------------------------------------------------------------------ */
.filiale-detail {
  max-width: 100%;
  margin: 0 auto;
  padding: $space-2 0;
  color: $clr-brand-gray;
}

/* Stati testuali compatti (caricamento/errore) */
.filiale-detail > div:not(.not-found):not(:has(.filiale-container)):not(:has(.filiale-sections)):not(:has(.filiale-footer)) {
  min-height: calc(100vh - var(--header-total-height) - var(--footer-total-height));
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $space-3 $container-padding-x;
  text-align: center;
  color: $text-color;
  font-size: $font-title;
  font-weight: 600;
  line-height: 1.2;
}

/* ------------------------------------------------------------------
   Container laterale come HTML component
------------------------------------------------------------------ */
.filiale-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 $container-padding-x;
  box-sizing: border-box;

  @media (min-width: 1025px) and (max-width: 1300px) { padding: 0 clamp(2rem, 4vw, 3rem); }
  @media (max-width: 1300px) { padding: 0 $container-padding-x; }
  @media (max-width: 1024px) { padding: 0 $space-1; }
  @media (max-width: 920px)  { padding: 0 $space-1; }
  @media (max-width: 768px)  { padding: 0 $space-1; }
  @media (max-width: 480px)  { padding: 0 $space-1; }
  @media (max-width: 360px)  { padding: 0 $space-0-75; }
}

/* Immagine filiale a tutta larghezza su mobile */
.filiale-hero-image {
  @media (max-width: 1024px) {
    :deep(.html-text-section > .container) {
      padding-left: 0;
      padding-right: 0;
    }

    :deep(.hero-image) {
      margin-bottom: $space-2;
    }

    :deep(.hero-image img) {
      width: 100%;
      max-height: 300px;
      object-fit: cover;
      box-shadow: none;
    }
  }
}

/* ------------------------------------------------------------------
   Header
------------------------------------------------------------------ */
.filiale-header {
  margin-bottom: $space-2;
}

.filiale-title {
  font-size: $font-title;
  font-weight: 300;
  color: $clr-brand-dark;
  margin: 0;
  line-height: 1.2;
}

/* ------------------------------------------------------------------
   Immagine
------------------------------------------------------------------ */
.filiale-image-wrapper {
  margin-bottom: $space-3;
}

.filiale-image {
  width: 100%;
  max-width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
}

/* ------------------------------------------------------------------
   Info base
------------------------------------------------------------------ */
.filiale-info {
  margin-bottom: $space-3;
}

.info-row {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: $space-1;
  padding: $space-0-75 0;
  border-bottom: 1px solid $clr-border;
  
  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 920px) {
    grid-template-columns: 150px 1fr;
    gap: $space-0-75;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: $space-0-5;
  }
  
  &--no-label {
    grid-template-columns: 1fr;
    
    .info-value {
      color: lighten($clr-brand-gray, 10%);
    }
  }
}

.info-label {
  font-size: $font-sm;
  font-weight: 600;
  color: $clr-brand-dark;
  line-height: 1.2;
}

.info-value {
  font-size: $font-sm;
  color: $clr-brand-gray;
  line-height: 1.4;
}

/* ------------------------------------------------------------------
   Address Row with Map Link on the right
------------------------------------------------------------------ */
.info-value--with-map {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  /* Contenitore con flex-wrap, ma con la ricaduta SEMPLICE, non a margini
     negativi. Misurato: i due figli restano nella stessa identica posizione
     con e senza ricaduta, quindi il gap qui non sposta niente - e'
     `justify-content: space-between` a distanziarli. I margini negativi
     costavano uno spostamento della scatola del contenitore di 16px in alto e
     a sinistra (952x60 -> 968x76) senza portare alcun beneficio.
     L'a-capo capita solo in una fascia stretta di larghezze fra i 480px e la
     larghezza a cui i due figli si toccherebbero: li' il secondo elemento
     resta rientrato di 16px, ed e' un costo accettato. */
  @include flex-gap($space-1, row, wrap-accettato);
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    flex-direction: column;
    /* qui diventa una colonna: con due soli elementi non va a capo, quindi
       basta la ricaduta semplice */
    @include flex-gap($space-0-5, column);
  }
}

.address-text {
  flex: 1;
  min-width: 0;
}

/* Maps Link inline with icon */
.maps-link-inline {
  display: inline-flex;
  align-items: center;
  @include flex-gap($space-0-5);
  font-size: $font-sm;
  font-weight: 600;
  color: $clr-tertiary;
  text-decoration: none;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;
  
  @media (hover: hover) {
    &:hover {
      color: darken($clr-tertiary, 10%);
      transform: translateX(3px);
    }
  }
  
  &:focus-visible {
    outline: 2px solid $clr-tertiary;
    outline-offset: 2px;
    border-radius: 4px;
    padding: 2px 4px;
  }
  /* ricaduta iOS < 15.4: senza :focus-visible la regola sopra viene
     scartata per intero. Si ripiega su :focus, che mostra l'anello
     anche col mouse: e' il degrado accettato. */
  .no-focusvisible &:focus {
    outline: 2px solid $clr-tertiary;
    outline-offset: 2px;
    border-radius: 4px;
    padding: 2px 4px;
  }
  
  @media (max-width: 480px) {
    white-space: normal;
  }
}

.maps-pin-icon {
  width: 1.1em;
  height: 1.1em;
  display: inline-block;
  flex-shrink: 0;
  object-fit: contain;
}

/* ------------------------------------------------------------------
   Orari - Bordo limitato al contenuto
------------------------------------------------------------------ */
.filiale-orari {
  margin-bottom: $space-2;
}

.orari-title {
  font-size: $font-h4;
  font-weight: 600;
  color: $clr-tertiary;
  margin-bottom: $space-0-5;
  line-height: 1.2;
}

.orari-grid {
  display: inline-block;
  min-width: 100%;
  
  @media (max-width: 600px) {
    display: block;
  }
}

.orari-row {
  display: inline-grid;
  grid-template-columns: 160px auto;
  gap: $space-1;
  padding: $space-0-75 $space-1;
  border-bottom: 1px solid $clr-border;
  min-width: 100%;
  
  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 600px) {
    display: grid;
    grid-template-columns: 1fr;
    gap: $space-0-5;
    padding: $space-0-75 0;
  }
}

.orari-label {
  font-size: $font-sm;
  font-weight: 600;
  color: $clr-brand-dark;
  line-height: 1.2;
}

.orari-time {
  font-size: $font-sm;
  color: $clr-brand-gray;
  white-space: nowrap;
  line-height: 1.2;
}

/* ------------------------------------------------------------------
   Sezioni dinamiche
------------------------------------------------------------------ */
.filiale-sections {
  margin: $space-2 0;
}

/* ------------------------------------------------------------------
   Footer
------------------------------------------------------------------ */
.filiale-footer {
  margin-top: $space-2;
  padding-top: $space-1-5;
  border-top: 1px solid $clr-border;
}

.filiale-footer .back-button {
  font-size: clamp(0.95rem, 2.5vw, 1.15rem);
  padding: 0.6rem 0;
}

/* ------------------------------------------------------------------
   Not found
------------------------------------------------------------------ */
.not-found {
  min-height: calc(100vh - var(--header-total-height) - var(--footer-total-height));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  @include flex-gap($space-0-5, column);
  padding: $space-3 $container-padding-x;

  h1 {
    font-size: $font-title;
    color: $clr-brand-dark;
    margin: 0;
    font-weight: 600;
    line-height: 1.2;
  }
  
  p {
    font-size: $font-lg;
    color: $clr-text-light;
    margin: 0;
    line-height: 1.35;
  }

  .back-button {
    margin-top: $space-1;
  }
}

/* ------------------------------------------------------------------
   Responsive padding laterale
------------------------------------------------------------------ */
@media (max-width: 1024px) {
  .filiale-detail { 
    padding: $space-2 0; 
  }
  .filiale-title { font-size: $font-title-lg; }
  .info-label, .info-value { font-size: $font-base-md; }
  .orari-title { font-size: $font-base-md; }
  .orari-label, .orari-time { font-size: $font-base-md; }
  .not-found p { font-size: $font-base-md; }
}
@media (max-width: 920px) {
  .filiale-detail { 
    padding: $space-2 0; 
  }
  .filiale-title { font-size: $font-title-sm; }
  .info-label, .info-value { font-size: $font-base-sm; }
  .orari-title { font-size: $font-base-sm; }
  .orari-label, .orari-time { font-size: $font-base-sm; }
  .not-found p { font-size: $font-base-sm; }
}
@media (max-width: 768px) {
  .filiale-detail { 
    padding: $space-2 0; 
  }
}
@media (max-width: 480px) {
  .filiale-title { font-size: $font-title-xs; }
  .info-label, .info-value { font-size: $font-base-xs; }
  .orari-title { font-size: $font-base-xs; }
  .orari-label, .orari-time { font-size: $font-base-xs; }
  .not-found h1 { font-size: $font-title-xs; }
  .not-found p { font-size: $font-base-xs; }
}
@media (max-width: 360px) {
  .filiale-title { font-size: $font-title-xxs; }
  .info-label, .info-value { font-size: $font-base-xxs; }
  .orari-title { font-size: $font-base-xxs; }
  .orari-label, .orari-time { font-size: $font-base-xxs; }
  .not-found h1 { font-size: $font-title-xxs; }
  .not-found p { font-size: $font-base-xxs; }
}

/* ──────────────────────────────────────────────────────────────
   ALLINEAMENTO AL PARAGRAFO
   titolo e contenuto partono dalla stessa x di "chi siamo":
   44px a 390, 39 a 360. Classe ripetuta per battere le regole annidate.
   ────────────────────────────────────────────────────────────── */
@media (max-width: 480px) {
  .filiale-title.filiale-title.filiale-title,
  .filiale-info.filiale-info.filiale-info {
    margin-left: 28px;
  }
}

@media (max-width: 360px) {
  .filiale-title.filiale-title.filiale-title,
  .filiale-info.filiale-info.filiale-info {
    margin-left: 27px;
  }
}
</style>
